import { useState } from "react";
import {
  CampaignChannel,
  CampaignDetails,
  CampaignMode,
  CampaignActionType,
  DbBusinessCampaign,
  TemplateParamSection,
} from "./models";
import { DbBusinessService } from "../category-service/models";
import { ExtendedTemplate } from "../whatsapp/message-templates/provider-state";
import { EmailTemplate } from "../email/models";
import { DbBusinessClient } from "../client/models";
import { Timestamp } from "firebase/firestore/lite";
import { clientService } from "../client/service";
import { DEFAULT_DIAL_CODE } from "canbook-data/util/country-codes";

export type CampaignTemplate = ExtendedTemplate | EmailTemplate;

export const initialCampaignDetails: CampaignDetails = {
  id: null,
  name: "",
  description: "",
  templateParams: null,
  previewText: "",
  filter: null,
  notifyingClients: [],
  media: null,
  createdAt: null,
  template: null,
  templateId: null,
  channel: CampaignChannel.Email,
  emailSubject: "",
  emailSender: "",
};

export const createDummyUser = (id: string): DbBusinessClient => ({
  id,
  name: "User Deleted",
  user_name: "User Deleted",
  phone: "-",
  email: "-",
  gender: null,
  created_at: new Timestamp(0, 0),
  profile_image_url: "",
  photos: [],
});

export function useCampaignState() {
  const [addCampaignCurrentStep, setAddCampaignCurrentStep] = useState(1);
  const [editCampaignCurrentStep, setEditCampaignCurrentStep] = useState(1);
  const [basicCampaignDetails, setBasicCampaignDetails] =
    useState<CampaignDetails>(initialCampaignDetails);
  const [templateDetails, setTemplateDetails] =
    useState<CampaignTemplate | null>(null);
  const [services, setServices] = useState<DbBusinessService[]>([]);
  const [filterDetails, setFilterDetails] = useState<any>(null);
  const [totalNotifiedClients, setTotalNotifiedClients] = useState<number>(0);
  const [clients, setClients] = useState<DbBusinessClient[]>([]);
  const [testPhone, setTestPhone] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_DIAL_CODE);
  const [sendingTestCampaign, setSendingTestCampaign] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [showSendCampaignModal, setShowSendCampaignModal] = useState(false);
  const [whatsappCredits, setWhatsappCredits] = useState<number>(0);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [campaignSendingMode, setCampaignSendingMode] =
    useState<CampaignMode>();
  const [toastText, setToastText] = useState<string>("");
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [actionType, setActionType] = useState<CampaignActionType>(
    CampaignActionType.Add,
  );
  const [campaign, setCampaign] = useState<DbBusinessCampaign | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ for: string; message: string }[]>([]);
  const [step1Errors, setStep1Errors] = useState<
    { for: string; message: string }[]
  >([]);
  const [step2Errors, setStep2Errors] = useState<
    { for: string; message: string }[]
  >([]);
  const [step3Errors, setStep3Errors] = useState<
    { for: string; message: string }[]
  >([]);
  const [step4Errors, setStep4Errors] = useState<
    { for: string; message: string }[]
  >([]);
  const [showingTemplates, setShowingTemplates] = useState<boolean>(false);
  const [isCopyingCampaign, setIsCopyingCampaign] = useState(false);

  const updateBasicCampaignField = (
    field: keyof CampaignDetails,
    value: any,
  ) => {
    setBasicCampaignDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetState = () => {
    setBasicCampaignDetails(initialCampaignDetails);
    setTemplateDetails(null);
    setFilterDetails(null);
    setServices([]);
    setTestPhone(null);
    setTestEmail(null);
    setCountryCode(DEFAULT_DIAL_CODE);
    setSendingTestCampaign(false);
    setShowSendCampaignModal(false);
    setWhatsappCredits(0);
    setShowBuyCreditModal(false);
    setIsLoading(true);
    setCampaignSendingMode(undefined);
    setTemplates([]);
    setActionType(CampaignActionType.Add);
    setCampaign(null);
    setError(null);
    setErrors([]);
    setStep1Errors([]);
    setStep2Errors([]);
    setStep3Errors([]);
    setStep4Errors([]);
    setShowingTemplates(false);
    setIsCopyingCampaign(false);
    setSendingTestCampaign(false);
    setAddCampaignCurrentStep(1);
    setEditCampaignCurrentStep(1);
  };

  return {
    addCampaignCurrentStep,
    setAddCampaignCurrentStep,
    editCampaignCurrentStep,
    setEditCampaignCurrentStep,
    basicCampaignDetails,
    setBasicCampaignDetails,
    templateDetails,
    setTemplateDetails,
    services,
    setServices,
    filterDetails,
    setFilterDetails,
    totalNotifiedClients,
    setTotalNotifiedClients,
    clients,
    setClients,
    testPhone,
    setTestPhone,
    countryCode,
    setCountryCode,
    testEmail,
    setTestEmail,
    sendingTestCampaign,
    setSendingTestCampaign,
    sendingCampaign,
    setSendingCampaign,
    showSendCampaignModal,
    setShowSendCampaignModal,
    toastText,
    setToastText,
    whatsappCredits,
    setWhatsappCredits,
    showBuyCreditModal,
    setShowBuyCreditModal,
    isLoading,
    setIsLoading,
    campaignSendingMode,
    setCampaignSendingMode,
    templates,
    setTemplates,
    actionType,
    setActionType,
    campaign,
    setCampaign,
    error,
    setError,
    errors,
    setErrors,
    step1Errors,
    setStep1Errors,
    step2Errors,
    setStep2Errors,
    step3Errors,
    setStep3Errors,
    step4Errors,
    setStep4Errors,
    showingTemplates,
    setShowingTemplates,
    isCopyingCampaign,
    setIsCopyingCampaign,
    updateBasicCampaignField,
    resetState,
  };
}

export const processFilteredClients = async (
  businessId: string,
  filteredClients: { client_id: string }[],
): Promise<DbBusinessClient[]> => {
  const clientIds = filteredClients.map((client) => client.client_id);
  const clients = await clientService.getBusinessClientsByIds(
    businessId,
    clientIds,
  );

  return clientIds.map((id) => {
    const client = clients.find((client) => client.id === id);
    if (!client) {
      return createDummyUser(id);
    }
    return client;
  });
};

export const getTemplateParams = (
  basicCampaignDetails: CampaignDetails,
): string[] => {
  return (
    (basicCampaignDetails.templateParams || [])
      .filter(
        (p: any) =>
          p.section === TemplateParamSection.Header ||
          !p.section ||
          p.section === TemplateParamSection.Data,
      )
      .sort((a: any, b: any) =>
        a.section === b.section
          ? 0
          : a.section === TemplateParamSection.Header
            ? -1
            : 1,
      )
      .map((param: any) => param.value) || []
  );
};
